/* eslint-disable complexity */
import { ReactNode, useEffect, useState } from "react";

import capitalize from "lodash/capitalize";
import { useIntl } from "react-intl";

import { SECTION_DIVIDER_VARIATION } from "@/components/adg4/section-divider/src/section-divider";
import { CarouselProps } from "@/components/confluence/carousel/src/carousel";
import { VideoCarouselProps } from "@/components/confluence/carousel/src/videoCarousel";
import { AnimatedJiraTourProps } from "@/components/product-demos/animated-jira-tour/src/animated-jira-tour";
import { IndividualTabPropsCustom } from "@/components/wpl/tab-group/src/tab-group";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src/data-event-props";
import { CtaWithPolygonBackgroundProps } from "@/patterns/adg4/cta-with-polygon-background/cta-with-polygon-background";
import ImageChecklistSide from "@/patterns/image-checklist-side/src/image-checklist-side";
import { PatternThreeAlternatingImageTextProps } from "@/patterns/three-alternating-image-text/src/three-alternating-image-text";
import {
    PaidLandingTemplate,
    PaidLandingProps
} from "@/templates/paid-landing/src/paid-landing.template";
import type {
    HeroProps,
    PaidLandingTypes
} from "@/templates/paid-landing/src/paid-landing.template";
import {
    AnchorContentsResponse,
    PaidLandingPersonaTypes
} from "@/utils/anchor-queries/get-anchor-contents";
import {
    JourneyLocalStoragePersona,
    useAnchorLocalStorage
} from "@/utils/local-storage-hook";

import { ConfluenceSoftwarePageProps } from "./confluence-software-page";
import {
    generateTabComponent,
    getAnimationByKeyword
} from "./keywords/keyword-util";
import {
    generateTabComponentSubAnchor,
    getAnimationBySubAnchor
} from "./subAnchors/subAnchor-util.tsx";

const LANDING_TYPE: PaidLandingTypes = "confluence-rebranded-anchor";

interface HeroConfiguration extends Partial<HeroProps> {
    useNewHero: boolean;
    showAnimatedHero: boolean;
    showScribble: boolean;
    showEyeLash: boolean;
    showFourSocialButtons: boolean;
}

export type PaidLandingPageVariants =
    | "brandUpdate"
    | "engineering"
    | "featuresCerebro"
    | "redesign-experiment"
    | "keywords"
    | "subAnchor"
    | "marketing"
    | "legal"
    | "hr"
    | "operations"
    | "design"
    | "programMgmt"
    | "customerService";

export type AnchorPageProps = {
    persona?: PaidLandingPersonaTypes;
    teamType?: JourneyLocalStoragePersona["team"];
    pageId: string;
    anchorContents?: AnchorContentsResponse | null;
    pageVariant: PaidLandingPageVariants;
    animatedJiraTourProps?: AnimatedJiraTourProps;
    dataProductKey: ProductKeyTypes;
    carouselHeroProps?: CarouselProps | VideoCarouselProps;
    customHeroSection?: ReactNode;
    customTabsProps?: PaidLandingProps["tabsProps"];
} & ConfluenceSoftwarePageProps;

export const ConfluencePagePaidLpConflAnchor = ({
    anchorContents,
    persona,
    teamType,
    pageId,
    pageVariant,
    animatedJiraTourProps,
    dataProductKey,
    carouselHeroProps,
    keyword,
    subAnchor,
    customHeroSection,
    customTabsProps
}: AnchorPageProps) => {
    const intl = useIntl();
    const productHref = useLocalizedHref("/software/confluence");
    const [content, setContent] = useState<AnchorContentsResponse | undefined>(
        undefined
    );

    const { setAnchorLocalStorage } = useAnchorLocalStorage();
    useEffect(() => {
        const anchorLocalStorageData: JourneyLocalStoragePersona = {
            persona,
            team: "",
            teamTypeInferredMl: teamType
        };
        if (!!keyword) {
            anchorLocalStorageData.keyword = keyword;
        }
        if (!!subAnchor) {
            anchorLocalStorageData.subAnchor = subAnchor;
        }
        setAnchorLocalStorage(anchorLocalStorageData);
        setContent(anchorContents);
        // We only want to run this useEffect once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!content) {
        // TODO: loading UI for static renders
        return <div />;
    }

    const props: PaidLandingProps = {
        hideNavigationBar: false,
        landingType: LANDING_TYPE,
        pageId,
        animatedJiraTourProps,
        showHorizontalRule: false,
        productNavProps: {
            productHref,
            productName: "confluence",
            ariaLabel: intl.formatMessage({
                id: "wacConfluenceNavigation.logo-label",
                defaultMessage: "Confluence logo"
            }),
            dataProductKey: "confluence",
            showFreeHrefButton: false
        },
        heroProps: getHeroProps({
            resp: content,
            pageVariant,
            keyword,
            subAnchor
        }),
        carouselHeroProps: carouselHeroProps,
        tabsProps:
            customTabsProps ||
            getTabProps(
                content,
                dataProductKey,
                pageVariant,
                keyword,
                subAnchor
            ),
        ctaWithPolyGonProps: getCtaWithPolygonProps(
            content,
            pageId,
            dataProductKey
        ),
        testimonialProps: getTestimonialProps(content),
        alternatingTextAndImageProps: getAlternatingTextAndImageProps(content),
        resultSectionDivider: {
            color: wacToken("color.neutral.light100"),
            variation: SECTION_DIVIDER_VARIATION.NORMAL
        },
        hasRebrandedSignupFormStyles: true,
        heroBgColor: "bgWhite",
        customHeroSection
    };
    return <PaidLandingTemplate {...props} />;
};

const heroFeatureMatrix: Record<string, HeroConfiguration> = {
    engineering: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: false,
        showStaticImage: true,
        showScribble: false,
        showEyeLash: false,
        showFourSocialButtons: false
    },
    featuresCerebro: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: false,
        showStaticImage: true,
        showScribble: false,
        showEyeLash: false,
        showFourSocialButtons: false
    },
    keywords: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: false,
        showStaticImage: true,
        showScribble: true,
        showEyeLash: true,
        showFourSocialButtons: false
    },
    subAnchor: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: false,
        showStaticImage: true,
        showScribble: true,
        showEyeLash: true,
        showFourSocialButtons: false
    },
    marketing: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: false,
        showStaticImage: true,
        showScribble: false,
        showEyeLash: false,
        showFourSocialButtons: false
    },
    brandUpdate: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: false,
        showStaticImage: false,
        showScribble: true,
        showEyeLash: true,
        showFourSocialButtons: true
    }
};

const getHeroFeatures = (pageVariant: string): HeroConfiguration => {
    const defaultFeatures: HeroConfiguration = {
        useNewHero: false,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: false,
        showStaticImage: false,
        showScribble: false,
        showEyeLash: false,
        showFourSocialButtons: false
    };

    return heroFeatureMatrix[pageVariant] || defaultFeatures;
};

interface getAnimationProps {
    pageVariant: string;
    keyword?: string;
    subAnchor?: string;
}

const getAnimation = ({
    pageVariant,
    keyword,
    subAnchor
}: getAnimationProps) => {
    if (pageVariant === "keywords") {
        return getAnimationByKeyword(keyword?.trim() || "");
    } else if (pageVariant === "subAnchor") {
        return getAnimationBySubAnchor(subAnchor?.trim() || "");
    }
    return undefined;
};

const getHeroProps = ({
    resp,
    pageVariant,
    keyword,
    subAnchor
}: {
    resp: AnchorContentsResponse;
    pageVariant: string;
    isUserLoggedIn?: boolean;
    keyword?: string;
    subAnchor?: string;
}): PaidLandingProps["heroProps"] => {
    const defaultProps: PaidLandingProps["heroProps"] = {
        theme: "dark-on-light",
        headerText: "",
        secondaryText: "",
        signup: {
            bundle: "confluence",
            edition: "free",
            featureFlags: {
                enableDefaultExpandedEmail: false,
                enableDeferredFullName: true
            }
        }
    };
    if (!resp || !resp[0]) {
        return defaultProps;
    }
    const { headline } = resp[0].node;
    const data = headline && headline[0] ? headline[0] : null;

    const planBenefits =
        data?.planBenefits && data?.planBenefits[0]
            ? data?.planBenefits[0]
            : null;
    const supportingAsset = planBenefits?.supportingAsset;
    const features = getHeroFeatures(pageVariant);

    const getImage = (url?: string | null, description?: string | null) =>
        url && description
            ? {
                  src: url ? url : "",
                  alt: description || ""
              }
            : undefined;

    const getFeaturesRawHtml = () =>
        planBenefits &&
        planBenefits.planFeatures &&
        planBenefits.planFeatures[0]
            ? planBenefits.planFeatures[0].description || ""
            : "";
    const animation = getAnimation({ pageVariant, keyword, subAnchor });

    return {
        ...defaultProps,
        ...features,
        headerText: data?.name || "",
        secondaryText: data?.subheading || "",
        image: getImage(supportingAsset?.url, supportingAsset?.description),
        featuresRawHtml: getFeaturesRawHtml(),
        tertiaryText: planBenefits?.description || "",
        animation
    };
};

const getTabProps = (
    resp: AnchorContentsResponse,
    dataProductKey: ProductKeyTypes,
    pageVariant?: PaidLandingPageVariants,
    keyword?: string,
    subAnchor?: string
): PaidLandingProps["tabsProps"] => {
    const defaultProps: PaidLandingProps["tabsProps"] = {
        dataProductKey: dataProductKey,
        headerText: "",
        tabStyles: {
            defaultBG: wacToken("color.neutral.light200"),
            // eslint-disable-next-line local/ensure-wac-token-usage
            defaultText: "#172B4D",
            hoverBG: wacToken("color.neutral.light200"),
            // eslint-disable-next-line local/ensure-wac-token-usage
            hoverText: "#172B4D",
            activeBG: wacToken("color.extended.blue100"),
            // eslint-disable-next-line local/ensure-wac-token-usage
            activeText: "#172B4D"
        },
        styles: {
            "--lp-tabs-hover": "#0C66E4",
            "--lp-tabs-unselected": "#172B4D"
        },
        tabs: undefined
    };

    if (!resp || !resp[0]) {
        return defaultProps;
    }

    const tabs = getTabs(resp, pageVariant, keyword, subAnchor);
    const primaryMessage = getPrimaryMessage(resp);

    return {
        ...defaultProps,
        landingType: LANDING_TYPE,
        headerText: primaryMessage?.name || "",
        tabs
    };
};

const getCtaWithPolygonProps = (
    resp: AnchorContentsResponse,
    pageId: string,
    dataProductKey: ProductKeyTypes
): CtaWithPolygonBackgroundProps => {
    const cta = getCta(resp);
    return {
        header: cta?.valueProposition || "",
        ctaText: cta?.text || "",
        ctaUrl: cta?.url || "",
        ctaLabel: pageId,
        backgroundColor: wacToken("color.extended.blue700") as "#1868DB",
        productKey: dataProductKey,
        buttonVariant: "primary",
        buttonColor: "yellow"
    };
};

const getTestimonialProps = (
    resp: AnchorContentsResponse
): PaidLandingProps["testimonialProps"] | undefined => {
    if (!resp || !resp[0]) {
        return undefined;
    }
    const { socialProof } = resp[0].node;
    const data = socialProof && socialProof[0] ? socialProof[0] : null;

    if (!data) {
        return undefined;
    }

    return {
        headerText: data?.name,
        logos: data?.customers?.map((customer) => {
            return {
                src:
                    customer &&
                    customer.altDarkLogo &&
                    customer?.altDarkLogo[0] &&
                    customer?.altDarkLogo[0].url
                        ? customer?.altDarkLogo[0].url
                        : "",
                alt:
                    customer &&
                    customer.altDarkLogo &&
                    customer?.altDarkLogo[0] &&
                    customer?.altDarkLogo[0].description
                        ? customer?.altDarkLogo[0].description
                        : "",
                lazy: true
            };
        })
    };
};

const getAlternatingTextAndImageProps = (
    resp: AnchorContentsResponse
): PatternThreeAlternatingImageTextProps | undefined => {
    const results = getResults(resp);
    function generateProps(
        result: {
            asset: {
                contentType: string | null;
                fileName: string | null;
                title: string | null;
                details: string | null;
                description: string | null;
                url: string | null;
            } | null;
            subheading: string | null;
            lozenge: string | null;
            name: string | null;
            id: string;
        } | null,
        index: number
    ) {
        // this shouldn't go past
        const countMap = ["one", "two", "three", "four"];
        const count = capitalize(`${countMap[index]}`);
        const position = index % 2 === 1 ? "left" : "right";

        return {
            backgroundColor: wacToken("color.neutral.light100"),
            [`row${count}Header`]: result?.name || "",
            [`row${count}Lozenge`]: result?.lozenge || "",
            [`row${count}Body`]: result?.subheading || "",
            [`row${count}ImageLink`]: result?.asset?.url,
            [`row${count}ImageAltText`]: result?.asset?.description || "",
            [`row${count}ImagePosition`]: position
        };
    }

    const contentBlocks = results
        ?.map((result, index) => generateProps(result, index))
        .slice(0, 4) // ensure max of 4 results so we don't break the pattern
        .filter((result) => !!result)
        .reduce((acc, curr) => {
            return {
                ...acc,
                ...curr
            };
        });

    return {
        imageWidth: "100%",
        imageTextSideRowOnePaddingBelowLargeScreen: "32px 0 16px",
        imageTextSideRowTwoPadding: "16px 0",
        imageTextSideRowThreePaddingBelowLargeScreen: "16px 0 32px",
        ...contentBlocks
    };
};

const getPrimaryMessage = (resp: AnchorContentsResponse) => {
    if (!resp || !resp[0]) {
        return null;
    }
    const { primaryMessage } = resp[0].node;
    const data = primaryMessage && primaryMessage[0] ? primaryMessage[0] : null;

    return data;
};

const getTabs = (
    resp: AnchorContentsResponse,
    pageVariant?: PaidLandingPageVariants,
    keyword?: string,
    subAnchor?: string
): IndividualTabPropsCustom[] | undefined => {
    const primaryMessage = getPrimaryMessage(resp);

    if (!primaryMessage || !primaryMessage?.supportingExample) {
        return undefined;
    }

    if (pageVariant === "keywords") {
        return primaryMessage.supportingExample
            .filter((tab) => tab !== null)
            .map((tab) => ({
                textLabel: "",
                id: "",
                component: generateTabComponent(keyword, {
                    title: primaryMessage?.name || "",
                    description: tab?.name || "",
                    content: tab?.proofpoint || ""
                })
            }));
    }
    if (pageVariant === "subAnchor") {
        return primaryMessage.supportingExample
            .filter((tab) => tab !== null)
            .map((tab) => ({
                textLabel: "",
                id: "",
                component: generateTabComponentSubAnchor(subAnchor, {
                    title: primaryMessage?.name || "",
                    description: tab?.name || "",
                    content: tab?.proofpoint || ""
                })
            }));
    }

    return primaryMessage.supportingExample
        .filter((tab) => tab !== null)

        .map((tab) => {
            const supportingAsset = tab?.supportingAsset;
            const advocateQuote = tab?.advocateQuote;

            return {
                textLabel: tab?.name || "",
                id: tab?.name || "",
                component: (
                    <ImageChecklistSide
                        isShowTestimonyBorder={false}
                        isIncludeTestimony={!!tab?.advocateQuote}
                        testimonyIsUsingTextQuotationMarks={true}
                        key={tab?.name}
                        title={tab?.heading || ""}
                        rawHtmlContent={tab?.proofpoint || ""}
                        imageSrc={supportingAsset?.url || ""}
                        imageAlt={supportingAsset?.description || ""}
                        quoteBlurb={advocateQuote?.advocateQuote || ""}
                        quoteName={advocateQuote?.advocateName || ""}
                        quoteCompany={advocateQuote?.advocateJobTitle || ""}
                        testimonyImageSrc={
                            advocateQuote?.advocateHeadshot?.url || ""
                        }
                        testimonyImageAlt={
                            advocateQuote?.advocateHeadshot?.description || ""
                        }
                    />
                )
            };
        });
};

const getResults = (resp: AnchorContentsResponse) => {
    if (!resp || !resp[0]) {
        return null;
    }
    const { results } = resp[0].node;
    const data = results && results.length ? results : null;

    return data;
};

const getCta = (resp: AnchorContentsResponse) => {
    if (!resp || !resp[0]) {
        return null;
    }
    const { callToAction } = resp[0].node;
    const data = callToAction && callToAction[0] ? callToAction[0] : null;

    return data;
};
